import type { AdminExtensions, PluginTranslation } from '../../shared/api'

export const EMPTY: AdminExtensions = {
  plugins: [],
  navigation: [],
  pages: [],
  dashboardWidgets: [],
  fieldWidgets: [],
}

export function pluginOf(ext: AdminExtensions, key: string) {
  return ext.plugins.find((p) => p.key === key)
}

/** Builds the frame URL for an extension; the host passes the target extension in the hash. */
export function frameUrl(adminUrl: string, kind: string, id: string) {
  return `${adminUrl}#${encodeURIComponent(kind)}=${encodeURIComponent(id)}`
}

/**
 * The language a plugin answers in (ADR-044): the admin language when the
 * plugin declares it (or its base language), else the plugin's default
 * language — its first declared one, English when it declares none.
 */
export function pluginLocale(
  plugin: { locales?: string[] } | undefined,
  uiLocale: string,
): string {
  const list = plugin?.locales?.length ? plugin.locales : ['en']

  if (list.includes(uiLocale)) return uiLocale
  const base = uiLocale.split('-')[0]

  return list.includes(base) ? base : list[0]
}

type Section = Exclude<keyof PluginTranslation, 'name' | 'description'>

/**
 * Localizes the manifest texts (plugin names, navigation labels, page and
 * widget titles) for the admin language: its translation, else the
 * plugin default language's translation, else the manifest text.
 */
export function localizeExtensions(
  ext: AdminExtensions,
  uiLocale: string,
): AdminExtensions {
  const byKey = new Map(ext.plugins.map((p) => [p.key, p]))

  const text = (
    pluginKey: string,
    section: Section | 'name',
    id: string,
    fallback: string,
  ) => {
    const p = byKey.get(pluginKey)
    const tr = p?.translations ?? {}

    const pick = (l: string | undefined) => {
      const entry = l ? tr[l] : undefined

      if (!entry) return undefined

      return section === 'name' ? entry.name : entry[section]?.[id]
    }

    return (
      pick(uiLocale) ??
      pick(uiLocale.split('-')[0]) ??
      pick(p?.locales?.[0]) ??
      fallback
    )
  }

  return {
    plugins: ext.plugins.map((p) => ({
      ...p,
      name: text(p.key, 'name', '', p.name),
    })),
    navigation: ext.navigation.map((n) => ({
      ...n,
      label: text(n.pluginKey, 'navigation', n.id, n.label),
    })),
    pages: ext.pages.map((x) => ({
      ...x,
      title: text(x.pluginKey, 'pages', x.id, x.title),
    })),
    dashboardWidgets: ext.dashboardWidgets.map((x) => ({
      ...x,
      title: text(x.pluginKey, 'dashboardWidgets', x.id, x.title),
    })),
    fieldWidgets: ext.fieldWidgets.map((x) => ({
      ...x,
      label: text(x.pluginKey, 'fieldWidgets', x.id, x.label),
    })),
  }
}

/**
 * Leaves out the extensions bound to a permission the user lacks (SDK 1.8,
 * ADR-071); a navigation item also goes when its page does. Only a matter
 * of what is shown: the plugin services check every request themselves.
 */
export function extensionsForUser(
  ext: AdminExtensions,
  permissions: ReadonlySet<string>,
): AdminExtensions {
  const allowed = (x: { permission?: string }) =>
    !x.permission || permissions.has(x.permission)

  const pages = ext.pages.filter(allowed)
  const pageIds = new Set(pages.map((x) => `${x.pluginKey}/${x.id}`))

  return {
    ...ext,
    pages,
    navigation: ext.navigation.filter(
      (n) => allowed(n) && pageIds.has(`${n.pluginKey}/${n.page}`),
    ),
    dashboardWidgets: ext.dashboardWidgets.filter(allowed),
  }
}
